import { ApplicationState, AppThunkDispatch } from "../store";
import { connect } from "react-redux";
import AppointmentForm  from "../components/Forms/AppointmentForm";
import { canEditAppointments, canEditClients, canSeePrices, getMyUser } from "../store/session/selectors";
import { AppointmentEntity, ClientEntity, ProfessionalEntity } from "../store/entities/state";
import { isSubmitting, getEntitiesDataItems, getChangeLog, getEntity, getChangingValue, getChangingState, getPacksForClient, getPacksForProfessional } from "../store/entities/selectors";
import { entityEditionCancel, updateEntity, deleteEntity, loadEntityChangeLog, loadEntities, updateChangingEntity, createEntity, openProfessional, openClientPage } from "../store/entities/actions";
import { updateAppointmentAsync, deleteAppointmentAsync, getAppointmentChangeLogAsync, getClientPacksAsync, createClientAsync, getProfessionalPacksAsync } from "../ajax/requests";
import { AppointmentFormDispatchProps, AppointmentFormStateProps } from "../components/Forms/AppointmentForm/AppointmentForm";
import { AppointmentChangeReason, ClientData } from "../models";
import { Guid } from "../shared/utils";

export interface AppointmentFormProps {
    data: AppointmentEntity,
    openPaymentModal?: boolean,
    createInvoice?: boolean,
}

const mapStateToProps = (state: ApplicationState, ownProps: AppointmentFormProps): AppointmentFormStateProps => {
    const data = getChangingValue(state, 'appointments') as AppointmentEntity
    return {
        type: 'edit',
        userProfessionalId: getMyUser(state)?.professionalId,
        canManageAppointments: canEditAppointments(state),
        canManageClients: canEditClients(state),
        canSeePrices: canSeePrices(state),
        canEditPayments: canSeePrices(state),
        canDeletePayments: canSeePrices(state),
        original: data ? getEntity(state, 'appointments', data.id) : undefined,
        data,
        clientPacks: data?.clientId ? getPacksForClient(data.clientId)(state) : undefined,
        professionalPacks: data?.professionalId ? getPacksForProfessional(data.professionalId)(state) : undefined,
        hasChanged: getChangingState(state, 'appointments'),
        isSubmitting: isSubmitting('appointments')(state),
        clients: getEntitiesDataItems<ClientEntity>('clients')(state),
        professionals: getEntitiesDataItems<ProfessionalEntity>('professionals')(state),
        changeLog: getChangeLog(state, 'appointments', ownProps.data.id),
        openPaymentModal: ownProps.openPaymentModal,
        createInvoice: ownProps.createInvoice,
    }
}

const mapDispatchToProps = (dispatch: AppThunkDispatch, ownProps: AppointmentFormProps): AppointmentFormDispatchProps => ({
    onChange: (data: AppointmentEntity) => dispatch(updateChangingEntity('appointments', data)),
    onCancel: () => dispatch(entityEditionCancel('appointments')),
    onSave: (data: AppointmentEntity, changeReason: AppointmentChangeReason | undefined, ignoreAvailability: boolean, updateEntireGroup?: boolean, keepOpen?: boolean) =>
        dispatch(updateEntity('appointments', data, data => updateAppointmentAsync(data.id, {data, changeReason}, ignoreAvailability, updateEntireGroup ?? false), keepOpen)),
    onDelete: (deleteEntireGroup?: boolean, reason?: any) => dispatch(deleteEntity(
        'appointments',
        ownProps.data.id,
        d => deleteAppointmentAsync(d, deleteEntireGroup ?? false, reason),
        deleteEntireGroup && ownProps.data.recurring?.groupId
            ? (state) => getEntitiesDataItems<AppointmentEntity>('appointments')(state)
                .filter(a => a.recurring?.groupId === ownProps.data.recurring?.groupId && a.date >= ownProps.data.date)
                .map(a => a.id)
            : undefined,
    )),
    onLoad: () => {
        dispatch(loadEntityChangeLog('appointments', ownProps.data.id, () => getAppointmentChangeLogAsync(ownProps.data.id)));
    },
    onOpenClient: (clientId: string) => {
        dispatch(entityEditionCancel('appointments'));
        dispatch(openClientPage(clientId));
    },
    onOpenProfessional: (professionalId: string) => {
        dispatch(entityEditionCancel('appointments'));
        dispatch(openProfessional(professionalId));
    },
    onLoadClientPacks: (clientId: string) => {
        dispatch(loadEntities('clientPacks', () => getClientPacksAsync(clientId), 'AppendResult'))
    },
    onLoadProfessionalPacks: (professionalId: string) => {
        dispatch(loadEntities('clientPacks', () => getProfessionalPacksAsync(professionalId), 'AppendResult'))
    },
    onCreateNewClient: (clientName: string, professionalId?: string) => {
        const client: ClientData = {id: Guid.newGuid(), name: clientName, professionalId}
        return dispatch(createEntity('clients', client, c => createClientAsync(c)))
    },
});

const AppointmentEditForm = connect<AppointmentFormStateProps, AppointmentFormDispatchProps, AppointmentFormProps, ApplicationState>(
    mapStateToProps,
    mapDispatchToProps,
)(AppointmentForm as any);

export default AppointmentEditForm;
